import { useEffect, useState } from 'react';
import '../Admin.scss';
import MainURL from '../../MainURL';
import axios from 'axios';
import ReactDOM from 'react-dom';
import Loading from '../../components/Loading';
import { extractEmail, parseInquiry, buildInquiryJson, renderPreview } from '../recruit/recruitSavedAdminShared';
import { ADMIN_MAIL_SENDER } from '../adminMailConstants';

const EMAIL_RULE =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@(([^<>()[\]\\.,;:\s@"]+\.)+[^<>()[\]\\.,;:\s@"]{2,})$/i;

const DEFAULT_TEST_EMAIL = 'johnlovesyou@naver.com';
const DEFAULT_MEMBER_COUNT = '2500';
const DEFAULT_DAILY_VISITOR_COUNT = '100';

const parseStatCountInput = (value: string) => value.replace(/,/g, '').trim();

const isValidStatCountInput = (value: string) => {
  const normalized = parseStatCountInput(value);
  if (!normalized) return false;

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed >= 0;
};

const recruitEmailSchoolList = [
  { name: '고려신학대학원', home: 'https://www.kts.ac.kr/home/' },
  { name: '광신대학교', home: 'https://www.kwangshin.ac.kr/home/index.do' },
  { name: '구세군군사관대학원대학교', home: 'https://gufot.ac.kr' },
  { name: '대전신학대학교', home: 'https://www.daejeon.ac.kr' },
  { name: '대신대학교', home: 'https://www.daeshin.ac.kr/html/05_community/03.php' },
  { name: '부산장신대학교', home: 'https://www.bpu.ac.kr/Default.aspx' },
  { name: '서울장신대학교', home: 'https://www.sjs.ac.kr' },
  { name: '서울신학대학교신대원', home: 'https://gs.stu.ac.kr/CmsHome/StuGs0207_0101.eznic' },
  { name: '성결교신대원', home: 'http://www.skts.org/' },
  { name: '영남신학대학교', home: 'https://www.ytus.ac.kr/html/index.php' },
  { name: '웨스트민스터신학대학원대학교', home: 'http://www.wgst.ac.kr/wgst_renew' },
  { name: '장로회신학대학교', home: 'https://www.puts.ac.kr' },
  { name: '총신대학교', home: 'https://www.csu.ac.kr/' },
  { name: '한세대학교', home: 'https://www.hansei.ac.kr/sites/kor/index.do' },
  { name: '한신대학교', home: 'https://www.hs.ac.kr/gradseoulcampus/index.do' },
  { name: '한일장신대학교', home: 'https://www.hanil.ac.kr' },
  { name: '한국침례신학대학교신대원', home: 'https://gtheology.kbtus.ac.kr/gtheology/Main.do' },
  { name: '호남신학대학교', home: 'https://www.htus.ac.kr/index.php#' },
];

type RecruitPreItem = {
  id: number | string;
  title: string;
  date: string;
  church: string;
  source?: string;
  inquiry?: string;
  link?: string;
  customInput?: string;
  isSentEmail?: string;
  isAccepted?: string;
  readAt?: string;
};

const isUnsentEmail = (value?: string) => !String(value ?? '').trim();

type SaveDenyItem = {
  id: number;
  church?: string;
  email?: string;
};

type RecruitEmailTab = 'list' | 'sent' | 'history' | 'school' | 'deny';

type MailSendLogItem = {
  id: number;
  startedAt: string;
  finishedAt?: string | null;
  totalCount: number;
  successCount: number;
  failCount: number;
  status: string;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
};

const getMailSendLogStatusLabel = (status?: string) => {
  switch (status) {
    case 'running':
      return '진행 중';
    case 'completed':
      return '완료';
    case 'failed':
      return '실패';
    default:
      return status || '-';
  }
};

const normalizeDenyEmail = (email?: string) => email?.trim().toLowerCase() || '';

const getDuplicateDenyEmailInfo = (items: SaveDenyItem[]) => {
  const emailToItems = new Map<string, SaveDenyItem[]>();

  for (const item of items) {
    const email = normalizeDenyEmail(item.email);
    if (!email) continue;

    const group = emailToItems.get(email) ?? [];
    group.push(item);
    emailToItems.set(email, group);
  }

  const duplicateIds = new Set<number>();
  const keptIds = new Set<number>();

  for (const group of Array.from(emailToItems.values())) {
    if (group.length <= 1) continue;

    const sorted = [...group].sort((a, b) => a.id - b.id);
    keptIds.add(sorted[0].id);
    sorted.slice(1).forEach((item) => duplicateIds.add(item.id));
  }

  return {
    duplicateIds,
    keptIds,
    duplicateCount: duplicateIds.size,
  };
};

function DetailModal({
  open,
  onClose,
  content,
}: {
  open: boolean;
  onClose: () => void;
  content: string;
}) {
  if (!open) return null;

  return ReactDOM.createPortal(
    <div
      className="admin-board-manage__modal-backdrop"
      onClick={onClose}
    >
      <div
        className="admin-board-manage__modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recruit-pre-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="admin-board-manage__modal-head">
          <h3 id="recruit-pre-detail-title">상세내용</h3>
          <button
            type="button"
            className="admin-board-manage__modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        <div className="admin-board-manage__modal-body">
          <div
            className="custom-html-content"
            dangerouslySetInnerHTML={{ __html: content || '<p>상세내용이 없습니다.</p>' }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
}

export default function RecruitEmailManage() {
  const [activeTab, setActiveTab] = useState<RecruitEmailTab>('list');
  const [list, setList] = useState<RecruitPreItem[]>([]);
  const [displayList, setDisplayList] = useState<RecruitPreItem[]>([]);
  const [sentList, setSentList] = useState<RecruitPreItem[]>([]);
  const [sendLogList, setSendLogList] = useState<MailSendLogItem[]>([]);
  const [sendLogTotalCount, setSendLogTotalCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sentTotalCount, setSentTotalCount] = useState(0);
  const [listLoading, setListLoading] = useState(true);
  const [sendLogLoading, setSendLogLoading] = useState(false);
  const [denyListItems, setDenyListItems] = useState<SaveDenyItem[]>([]);
  const [denyListLoading, setDenyListLoading] = useState(true);
  const [denyDedupeLoading, setDenyDedupeLoading] = useState(false);
  const [denyAddLoading, setDenyAddLoading] = useState(false);
  const [denyAddEmail, setDenyAddEmail] = useState('');
  const [denyAddChurch, setDenyAddChurch] = useState('');
  const [listCleanupLoading, setListCleanupLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [testEmail, setTestEmail] = useState(DEFAULT_TEST_EMAIL);
  const [memberCount, setMemberCount] = useState(DEFAULT_MEMBER_COUNT);
  const [dailyVisitorCount, setDailyVisitorCount] = useState(DEFAULT_DAILY_VISITOR_COUNT);
  const [cleanupStats, setCleanupStats] = useState({ duplicateCount: 0, denyCount: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});
  const [emailSavingId, setEmailSavingId] = useState<number | string | null>(null);

  const fetchDenySaveList = async () => {
    setDenyListLoading(true);
    try {
      const res = await axios.get(`${MainURL}/recruitwork/getsavedenylist`);
      const items: SaveDenyItem[] = Array.isArray(res.data?.resultData) ? res.data.resultData : [];
      setDenyListItems(items);
    } catch (error) {
      console.error('전송금지 목록 조회 오류:', error);
      setDenyListItems([]);
    } finally {
      setDenyListLoading(false);
    }
  };

  const fetchCleanupStats = async () => {
    try {
      const res = await axios.get(`${MainURL}/recruitwork/getrecruitprecleanupstats`);
      if (res.data?.success) {
        setCleanupStats({
          duplicateCount: Number(res.data.duplicateCount) || 0,
          denyCount: Number(res.data.denyCount) || 0,
        });
      }
    } catch (error) {
      console.error('정리 대상 통계 조회 오류:', error);
    }
  };

  const mapRecruitPreRows = (raw: unknown): RecruitPreItem[] => {
    const rows = Array.isArray(raw) ? raw : raw ? [raw] : [];
    return rows.map((item) => ({
      id: item.id,
      title: item.title || '',
      date: item.date || '',
      church: item.church || '',
      source: item.source || '',
      inquiry: item.inquiry || '',
      link: item.link || '',
      customInput: item.customInput || '',
      isSentEmail: item.isSentEmail || '',
      isAccepted: item.isAccepted || '',
      readAt: item.readAt || '',
    }));
  };

  const fetchSendLogs = async () => {
    setSendLogLoading(true);
    try {
      const res = await axios.get(`${MainURL}/recruitwork/getrecruitpremailsendlogs?limit=100&page=1`);
      const raw = res.data?.resultData;
      const rows: MailSendLogItem[] = (Array.isArray(raw) ? raw : raw ? [raw] : []).map((item) => ({
        id: Number(item.id),
        startedAt: item.startedAt || '',
        finishedAt: item.finishedAt || '',
        totalCount: Number(item.totalCount) || 0,
        successCount: Number(item.successCount) || 0,
        failCount: Number(item.failCount) || 0,
        status: item.status || '',
      }));

      setSendLogList(rows);
      setSendLogTotalCount(Number(res.data?.totalCount) || rows.length);
    } catch (error) {
      console.error('발송 이력 조회 오류:', error);
      setSendLogList([]);
      setSendLogTotalCount(0);
    } finally {
      setSendLogLoading(false);
    }
  };

  const fetchPosts = async (sentOnly = false) => {
    setListLoading(true);
    try {
      const query = sentOnly
        ? 'limit=100&page=1&sentOnly=1'
        : 'limit=100&page=1&pendingOnly=1';
      const res = await axios.get(`${MainURL}/recruitwork/getrecruitmanagepre?${query}`);
      const rows = mapRecruitPreRows(res.data?.resultData);

      if (sentOnly) {
        setSentList(rows);
        setSentTotalCount(Number(res.data?.totalCount) || rows.length);
      } else {
        setList(rows);
        setTotalCount(Number(res.data?.totalCount) || rows.length);
      }
    } catch (error) {
      console.error('구인 목록 조회 오류:', error);
      if (sentOnly) {
        setSentList([]);
        setSentTotalCount(0);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => {
    void fetchDenySaveList();
    void fetchCleanupStats();
  }, []);

  useEffect(() => {
    if (activeTab === 'list') {
      void fetchPosts(false);
    } else if (activeTab === 'sent') {
      void fetchPosts(true);
    } else if (activeTab === 'history') {
      void fetchSendLogs();
    }
  }, [activeTab]);

  useEffect(() => {
    const denyChurches = new Set(
      denyListItems.map((item) => item.church).filter((church): church is string => Boolean(church))
    );
    const denyEmails = new Set(
      denyListItems.map((item) => item.email).filter((email): email is string => Boolean(email))
    );

    const filtered = list.filter((item) => {
      if (!isUnsentEmail(item.isSentEmail)) return false;

      if (item.church && denyChurches.has(item.church)) return false;

      const email = extractEmail(item.inquiry);
      if (email && denyEmails.has(email)) return false;

      return true;
    });

    setDisplayList(filtered);
  }, [list, denyListItems]);

  const denyDuplicateInfo = getDuplicateDenyEmailInfo(denyListItems);

  const getEmailDraft = (item: RecruitPreItem) => {
    const key = String(item.id);
    if (key in emailDrafts) return emailDrafts[key];
    return extractEmail(item.inquiry);
  };

  const handleEmailDraftChange = (id: number | string, value: string) => {
    setEmailDrafts((prev) => ({ ...prev, [String(id)]: value }));
  };

  const handleSaveInquiryEmail = async (item: RecruitPreItem) => {
    const email = getEmailDraft(item).trim();

    if (!EMAIL_RULE.test(email)) {
      window.alert('유효하지 않은 이메일 형식입니다.');
      return;
    }

    setEmailSavingId(item.id);
    try {
      const res = await axios.post(`${MainURL}/recruitwork/updaterecruitpreinquiryemail`, {
        id: item.id,
        email,
      });

      if (res.data?.success) {
        const updatedInquiry =
          typeof res.data.inquiry === 'string'
            ? res.data.inquiry
            : buildInquiryJson({ ...parseInquiry(item.inquiry), email });

        setList((prev) =>
          prev.map((row) => (row.id === item.id ? { ...row, inquiry: updatedInquiry } : row))
        );
        setEmailDrafts((prev) => {
          const next = { ...prev };
          delete next[String(item.id)];
          return next;
        });
      } else {
        window.alert(res.data?.message || '이메일 수정에 실패했습니다.');
      }
    } catch {
      window.alert('서버 오류로 이메일 수정에 실패했습니다.');
    } finally {
      setEmailSavingId(null);
    }
  };

  const handleDeleteRow = async (id: number | string) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      const res = await axios.post(`${MainURL}/recruitwork/deleterecruitpre`, { id });
      if (res.data?.success) {
        if (activeTab === 'sent') {
          setSentList((prev) => prev.filter((item) => item.id !== id));
          setSentTotalCount((prev) => Math.max(0, prev - 1));
        } else {
          setList((prev) => prev.filter((item) => item.id !== id));
          setTotalCount((prev) => Math.max(0, prev - 1));
        }
      } else {
        window.alert('삭제에 실패했습니다.');
      }
    } catch {
      window.alert('서버 오류로 삭제에 실패했습니다.');
    }
  };

  const handleCleanupRecruitPre = async () => {
    if (
      !window.confirm(
        'recruitDataPre에서 아래 항목을 삭제합니다.\n· 이메일 없음\n· 문의 이메일 중복(각 이메일당 1건만 유지)\n· 전송금지목록(saveDenyList)에 있는 교회·이메일\n\n계속하시겠습니까?'
      )
    ) {
      return;
    }

    setListCleanupLoading(true);
    try {
      const res = await axios.post(`${MainURL}/recruitwork/cleanuprecruitpre`);
      if (res.data?.success) {
        const deletedCount = Number(res.data.deletedCount) || 0;
        const noEmailCount = Number(res.data.noEmailCount) || 0;
        const duplicateCount = Number(res.data.duplicateCount) || 0;
        const denyCount = Number(res.data.denyCount) || 0;

        if (deletedCount > 0) {
          window.alert(
            `총 ${deletedCount}건 삭제했습니다.\n· 이메일 없음: ${noEmailCount}건\n· 중복 이메일: ${duplicateCount}건\n· 전송금지: ${denyCount}건`
          );
        } else {
          window.alert('삭제할 항목이 없습니다.');
        }

        await fetchPosts(false);
        await fetchCleanupStats();
      } else {
        window.alert('중복삭제 및 정리에 실패했습니다.');
      }
    } catch {
      window.alert('서버 오류로 중복삭제 및 정리에 실패했습니다.');
    } finally {
      setListCleanupLoading(false);
    }
  };

  const buildMailItem = (row: RecruitPreItem) => {
    const email = extractEmail(row.inquiry).trim();

    return {
      id: row.id,
      title: row.title,
      church: row.church,
      source: row.source || '',
      saveDate: row.date,
      date: row.date,
      email,
      inquiry: row.inquiry,
      isSentEmail: row.isSentEmail || '',
    };
  };

  const getUniqueMailTargets = () => {
    const seen = new Set<string>();

    return displayList
      .map(buildMailItem)
      .filter((item) => isUnsentEmail(item.isSentEmail))
      .filter((item) => EMAIL_RULE.test(item.email))
      .filter((item) => {
        const key = item.email.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  };

  const buildMailStatsPayload = () => ({
    memberCount: parseStatCountInput(memberCount),
    dailyVisitorCount: parseStatCountInput(dailyVisitorCount),
  });

  const validateMailStatsInput = () => {
    if (!isValidStatCountInput(memberCount) || !isValidStatCountInput(dailyVisitorCount)) {
      window.alert('회원 수와 일일 방문자 수에 0 이상의 숫자를 입력해 주세요.');
      return false;
    }

    return true;
  };

  const handleTestSendEmail = async () => {
    const recipient = testEmail.trim();

    if (!EMAIL_RULE.test(recipient)) {
      window.alert('유효한 테스트 수신 이메일을 입력해 주세요.');
      return;
    }

    if (!validateMailStatsInput()) {
      return;
    }

    const previewItem = getUniqueMailTargets()[0] || displayList[0];

    const confirmMessage = [
      '청빙 공고 안내 테스트 메일을 1건만 발송합니다.',
      '(미리보기 공고가 사이트에 옮겨져 있으면 「공고를 옮겨 올렸습니다」 안내, 아니면 등록 제안 본문)',
      '',
      `발신: ${ADMIN_MAIL_SENDER}`,
      `수신: ${recipient}`,
      '수신확인용 recruitDataPre 테스트 행이 자동 생성됩니다.',
      '「사역자모아 바로가기」 또는 홈페이지 링크 클릭 시 isAccepted 가 true 로 기록됩니다.',
      '발송 상태(isSentEmail)는 변경되지 않습니다.',
      previewItem
        ? `메일 본문 미리보기: ${previewItem.church || previewItem.title || '리스트 첫 항목'}`
        : '메일 본문: 기본값(귀 교회)',
    ].join('\n');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    setIsSending(true);
    try {
      const previewSource = previewItem?.source?.trim() || '';
      const res = await axios.post(`${MainURL}/recruitwork/sendtestrecruitpremail`, {
        testEmail: recipient,
        previewId: previewItem?.id ?? null,
        source: previewSource,
        church: previewItem?.church?.trim() || '',
        ...buildMailStatsPayload(),
      });

      if (res.data?.success) {
        const lines = [
          res.data.message,
          `발신: ${res.data.from || ADMIN_MAIL_SENDER}`,
          res.data.messageId ? `메시지 ID: ${res.data.messageId}` : '',
          res.data.trackRecruitDataPreId
            ? `수신확인 연결 ID: ${res.data.trackRecruitDataPreId}`
            : '',
          '',
          '받은편지함에 없으면 스팸함을 확인해 주세요.',
          '메일에서 「사역자모아 바로가기」 또는 홈페이지 링크를 클릭한 뒤,',
          '전송완료 탭에서 수신확인(isAccepted)이 true 로 바뀌는지 확인하세요.',
          res.data.subject ? `제목: ${res.data.subject}` : '',
        ].filter(Boolean);
        window.alert(lines.join('\n'));
      } else {
        window.alert(res.data?.message || '테스트 이메일 발송에 실패했습니다.');
      }
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : error instanceof Error
            ? error.message
            : '알 수 없는 오류';
      window.alert(`테스트 이메일 발송 중 오류가 발생했습니다.\n${message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleBulkSendEmail = async () => {
    const mailTargets = getUniqueMailTargets();

    if (mailTargets.length === 0) {
      window.alert('발송할 이메일이 없습니다.');
      return;
    }

    if (!validateMailStatsInput()) {
      return;
    }

    if (
      !window.confirm(
        `현재 리스트 ${mailTargets.length}건에 청빙 공고 안내 메일을 발송하시겠습니까?\n(사이트에 옮겨진 공고는 「옮겨 올렸습니다」 안내, 아니면 등록 제안)\n발신: ${ADMIN_MAIL_SENDER}\n(미발송 대상만, 한 번에 최대 100건, 백그라운드 순차 발송, 발송 후 isSentEmail=true로 표시)`
      )
    ) {
      return;
    }

    setIsSending(true);
    try {
      const res = await axios.post(`${MainURL}/recruitwork/sendbulkrecruitpremail`, {
        emailList: mailTargets,
        ...buildMailStatsPayload(),
      });

      if (res.data?.success) {
        const totalCount = Number(res.data.totalCount) || mailTargets.length;
        window.alert(
          res.data.message ||
            `메일 발송을 시작했습니다. ${totalCount}건이 백그라운드에서 순차적으로 발송됩니다.`
        );
      } else {
        window.alert(res.data?.message || '메일 발송 요청에 실패했습니다.');
      }
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : error instanceof Error
            ? error.message
            : '알 수 없는 오류';
      window.alert(`메일 발송 요청에 실패했습니다.\n${message}`);
    } finally {
      setIsSending(false);
    }
  };

  const handleAddDenyEmail = async () => {
    const email = denyAddEmail.trim();
    const church = denyAddChurch.trim();

    if (!EMAIL_RULE.test(email)) {
      window.alert('유효하지 않은 이메일 형식입니다.');
      return;
    }

    setDenyAddLoading(true);
    try {
      const res = await axios.post(`${MainURL}/recruitwork/addsavedenylist`, {
        email,
        church,
      });

      if (res.data?.success) {
        setDenyAddEmail('');
        setDenyAddChurch('');
        await fetchDenySaveList();
        await fetchCleanupStats();
      } else {
        window.alert(res.data?.message || '전송금지 목록 추가에 실패했습니다.');
      }
    } catch (error: unknown) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : '서버 오류로 전송금지 목록 추가에 실패했습니다.';
      window.alert(message);
    } finally {
      setDenyAddLoading(false);
    }
  };

  const handleDeduplicateDenyEmails = async () => {
    if (denyDuplicateInfo.duplicateCount === 0) {
      window.alert('중복된 이메일이 없습니다.');
      return;
    }

    if (
      !window.confirm(
        `중복 이메일 ${denyDuplicateInfo.duplicateCount}건을 삭제하고, 각 이메일당 1건만 남깁니다. 계속하시겠습니까?`
      )
    ) {
      return;
    }

    setDenyDedupeLoading(true);
    try {
      const res = await axios.post(`${MainURL}/recruitwork/deduplicatesavedenyemails`);
      if (res.data?.success) {
        const deletedCount = Number(res.data.deletedCount) || 0;
        window.alert(deletedCount > 0 ? `${deletedCount}건의 중복 항목을 삭제했습니다.` : '삭제할 중복 항목이 없습니다.');
        await fetchDenySaveList();
        await fetchCleanupStats();
      } else {
        window.alert('중복 이메일 삭제에 실패했습니다.');
      }
    } catch {
      window.alert('서버 오류로 중복 이메일 삭제에 실패했습니다.');
    } finally {
      setDenyDedupeLoading(false);
    }
  };

  const mailTargetCount = getUniqueMailTargets().length;

  return (
    <div className="admin-board-manage recruit-list-manage-pre">
      <div className="inner">
        <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#475569' }}>
          발신·문의 주소: <strong>{ADMIN_MAIL_SENDER}</strong> (Naver SMTP)
        </p>

        <div className="recruit-email-manage__tabs" role="tablist" aria-label="메일 전송 탭">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'list'}
            className={`recruit-email-manage__tab${activeTab === 'list' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('list')}
          >
            전송예정
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'sent'}
            className={`recruit-email-manage__tab${activeTab === 'sent' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('sent')}
          >
            전송완료
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'history'}
            className={`recruit-email-manage__tab${activeTab === 'history' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            전송이력
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'deny'}
            className={`recruit-email-manage__tab${activeTab === 'deny' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('deny')}
          >
            전송금지목록
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'school'}
            className={`recruit-email-manage__tab${activeTab === 'school' ? ' is-active' : ''}`}
            onClick={() => setActiveTab('school')}
          >
            신학교
          </button>
        </div>

        {activeTab === 'list' && (
          <>
            <p className="admin-board-manage__desc" style={{ marginBottom: 16 }}>
              전송예정 {totalCount}건
              {list.length !== displayList.length
                ? ` · 표시 ${displayList.length}건 (전송금지 교회·이메일 제외)`
                : ` · 표시 ${displayList.length}건`}
            </p>

            <p className="recruit-email-manage__cleanup-summary">
              중복 이메일 <strong>{cleanupStats.duplicateCount}</strong>건 · 전송금지목록{' '}
              <strong>{cleanupStats.denyCount}</strong>건
            </p>

            <div className="recruit-email-manage__list-toolbar">
              <div className="recruit-email-manage__list-toolbar-row">
                <button
                  type="button"
                  className="admin-btn"
                  disabled={listLoading || listCleanupLoading || isSending}
                  onClick={() => void fetchPosts(false)}
                >
                  {listLoading ? '불러오는 중...' : '새로고침'}
                </button>
                <button
                  type="button"
                  className="admin-btn delete-btn"
                  disabled={listLoading || listCleanupLoading || isSending}
                  onClick={() => void handleCleanupRecruitPre()}
                >
                  {listCleanupLoading ? '처리 중...' : '중복삭제 및 정리'}
                </button>
                <button
                  type="button"
                  className="admin-btn send-btn"
                  disabled={listLoading || listCleanupLoading || isSending || mailTargetCount === 0}
                  onClick={() => void handleBulkSendEmail()}
                >
                  {isSending ? '발송 중...' : `메일보내기 (${mailTargetCount})`}
                </button>
              </div>
              <div className="recruit-email-manage__list-toolbar-row recruit-email-manage__list-toolbar-row--stats">
                <label className="recruit-email-manage__test-email-field recruit-email-manage__stat-field">
                  <span>회원 수</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={memberCount}
                    onChange={(e) => setMemberCount(e.target.value)}
                    disabled={listLoading || listCleanupLoading || isSending}
                    placeholder="2500"
                  />
                </label>
                <label className="recruit-email-manage__test-email-field recruit-email-manage__stat-field">
                  <span>일일 방문</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={dailyVisitorCount}
                    onChange={(e) => setDailyVisitorCount(e.target.value)}
                    disabled={listLoading || listCleanupLoading || isSending}
                    placeholder="100"
                  />
                </label>
                <div className="recruit-email-manage__test-send-group">
                  <label className="recruit-email-manage__test-email-field">
                    <span>테스트 수신</span>
                    <input
                      type="email"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      disabled={listLoading || listCleanupLoading || isSending}
                      placeholder="test@example.com"
                    />
                  </label>
                  <button
                    type="button"
                    className="admin-btn test-btn"
                    disabled={listLoading || listCleanupLoading || isSending}
                    onClick={() => void handleTestSendEmail()}
                  >
                    {isSending ? '발송 중...' : '테스트 발송'}
                  </button>
                </div>
              </div>
            </div>

            {listLoading ? (
              <Loading />
            ) : (
              <>
                <div className="admin-board-manage__table-wrap">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>제목</th>
                    <th>등록일</th>
                    <th>교회명</th>
                    <th>출처</th>
                    <th>문의</th>
                    <th className="admin-board-manage__col-actions">관리</th>
                  </tr>
                </thead>
                <tbody>
                  {displayList.length > 0 ? (
                    displayList.map((item) => {
                      const email = extractEmail(item.inquiry);
                      const emailDraft = getEmailDraft(item);
                      const isEmailInvalid = Boolean(emailDraft.trim()) && !EMAIL_RULE.test(emailDraft.trim());
                      const isEmailDirty = emailDraft.trim() !== email.trim();
                      const isSavingEmail = emailSavingId === item.id;

                      return (
                        <tr key={item.id}>
                          <td style={{ textAlign: 'left', maxWidth: 360 }} title={item.title}>
                            {item.title || '-'}
                          </td>
                          <td>{item.date || '-'}</td>
                          <td title={item.church}>{item.church || '-'}</td>
                          <td title={item.source}>{renderPreview(item.source || '-', 16)}</td>
                          <td
                            className="admin-board-manage__account"
                            style={{ textAlign: 'left', maxWidth: 320 }}
                          >
                            <div className="recruit-email-manage__email-edit">
                              <input
                                type="email"
                                className={`recruit-email-manage__email-input${isEmailInvalid ? ' is-invalid' : ''}`}
                                value={emailDraft}
                                onChange={(e) => handleEmailDraftChange(item.id, e.target.value)}
                                disabled={listLoading || listCleanupLoading || isSending || isSavingEmail}
                                placeholder="email@example.com"
                              />
                              <button
                                type="button"
                                className="admin-btn view-btn"
                                disabled={
                                  listLoading ||
                                  listCleanupLoading ||
                                  isSending ||
                                  isSavingEmail ||
                                  !isEmailDirty ||
                                  isEmailInvalid
                                }
                                onClick={() => void handleSaveInquiryEmail(item)}
                              >
                                {isSavingEmail ? '저장 중...' : '저장'}
                              </button>
                            </div>
                          </td>
                          <td className="admin-board-manage__col-actions">
                            <div className="admin-board-manage__actions">
                              <button
                                type="button"
                                className="admin-btn view-btn"
                                onClick={() => {
                                  setModalContent(item.customInput || '');
                                  setModalOpen(true);
                                }}
                              >
                                상세
                              </button>
                              <button
                                type="button"
                                className="admin-btn delete-btn"
                                onClick={() => void handleDeleteRow(item.id)}
                              >
                                삭제
                              </button>
                              <button
                                type="button"
                                className="admin-btn"
                                style={{
                                  background: item.link ? '#6c5ce7' : '#ccc',
                                  color: '#fff',
                                  cursor: item.link ? 'pointer' : 'not-allowed',
                                }}
                                disabled={!item.link}
                                onClick={() => {
                                  if (item.link) {
                                    window.open(item.link, '_blank', 'noopener,noreferrer');
                                  }
                                }}
                              >
                                링크
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                        등록된 글이 없습니다.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
              </>
            )}
          </>
        )}

        {activeTab === 'sent' && (
          <>
            <p className="admin-board-manage__desc" style={{ marginBottom: 16 }}>
              전송완료 {sentTotalCount}건
              {sentList.length !== sentTotalCount
                ? ` · 표시 ${sentList.length}건 (최대 100건)`
                : ` · 표시 ${sentList.length}건`}
            </p>

            <div className="recruit-email-manage__list-toolbar">
              <div className="recruit-email-manage__list-toolbar-row">
                <button
                  type="button"
                  className="admin-btn"
                  disabled={listLoading}
                  onClick={() => void fetchPosts(true)}
                >
                  {listLoading ? '불러오는 중...' : '새로고침'}
                </button>
              </div>
            </div>

            {listLoading ? (
              <Loading />
            ) : (
              <div className="admin-board-manage__table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>제목</th>
                      <th>등록일</th>
                      <th>교회명</th>
                      <th>출처</th>
                      <th>문의</th>
                      <th>링크 확인</th>
                      <th className="admin-board-manage__col-actions">관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sentList.length > 0 ? (
                      sentList.map((item) => {
                        const email = extractEmail(item.inquiry);

                        return (
                          <tr key={item.id}>
                            <td style={{ textAlign: 'left', maxWidth: 360 }} title={item.title}>
                              {item.title || '-'}
                            </td>
                            <td>{item.date || '-'}</td>
                            <td title={item.church}>{item.church || '-'}</td>
                            <td title={item.source}>{renderPreview(item.source || '-', 16)}</td>
                            <td
                              className="admin-board-manage__account"
                              style={{ textAlign: 'left', maxWidth: 280 }}
                              title={email}
                            >
                              {email || '-'}
                            </td>
                            <td>{item.isAccepted || '-'}</td>
                            <td className="admin-board-manage__col-actions">
                              <div className="admin-board-manage__actions">
                                <button
                                  type="button"
                                  className="admin-btn view-btn"
                                  onClick={() => {
                                    setModalContent(item.customInput || '');
                                    setModalOpen(true);
                                  }}
                                >
                                  상세
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn delete-btn"
                                  onClick={() => void handleDeleteRow(item.id)}
                                >
                                  삭제
                                </button>
                                <button
                                  type="button"
                                  className="admin-btn"
                                  style={{
                                    background: item.link ? '#6c5ce7' : '#ccc',
                                    color: '#fff',
                                    cursor: item.link ? 'pointer' : 'not-allowed',
                                  }}
                                  disabled={!item.link}
                                  onClick={() => {
                                    if (item.link) {
                                      window.open(item.link, '_blank', 'noopener,noreferrer');
                                    }
                                  }}
                                >
                                  링크
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                          전송 완료된 항목이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'history' && (
          <>
            <p className="admin-board-manage__desc" style={{ marginBottom: 16 }}>
              전송이력 {sendLogTotalCount}건
              {sendLogList.length !== sendLogTotalCount
                ? ` · 표시 ${sendLogList.length}건 (최대 100건)`
                : ` · 표시 ${sendLogList.length}건`}
            </p>

            <div className="recruit-email-manage__list-toolbar">
              <div className="recruit-email-manage__list-toolbar-row">
                <button
                  type="button"
                  className="admin-btn"
                  disabled={sendLogLoading}
                  onClick={() => void fetchSendLogs()}
                >
                  {sendLogLoading ? '불러오는 중...' : '새로고침'}
                </button>
              </div>
            </div>

            {sendLogLoading ? (
              <Loading />
            ) : (
              <div className="admin-board-manage__table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>시작 일시</th>
                      <th>대상 건수</th>
                      <th>성공</th>
                      <th>실패</th>
                      <th>완료 일시</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sendLogList.length > 0 ? (
                      sendLogList.map((item, index) => (
                        <tr key={item.id}>
                          <td>{sendLogList.length - index}</td>
                          <td>{formatDateTime(item.startedAt)}</td>
                          <td>{item.totalCount}</td>
                          <td>{item.successCount}</td>
                          <td>{item.failCount}</td>
                          <td>{formatDateTime(item.finishedAt)}</td>
                          <td>
                            <span
                              className={`recruit-email-manage__send-log-status recruit-email-manage__send-log-status--${item.status || 'unknown'}`}
                            >
                              {getMailSendLogStatusLabel(item.status)}
                            </span>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                          저장된 전송 이력이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        <DetailModal
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          content={modalContent}
        />

        {activeTab === 'school' && (
          <div className="recruit-email-manage__school-section">
            <h3 className="recruit-email-manage__school-title">신학교 홈페이지</h3>
            <div className="recruit-email-manage__school-grid">
              {recruitEmailSchoolList.map((item) => (
                <div key={item.name} className="recruit-email-manage__school-item">
                  <button
                    type="button"
                    className="recruit-email-manage__school-button"
                    onClick={() => {
                      if (item.home) {
                        window.open(item.home, '_blank', 'noopener,noreferrer');
                      }
                    }}
                  >
                    <div className="recruit-email-manage__school-name">
                      <p>{item.name}</p>
                    </div>
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'deny' && (
          <>
            <div className="recruit-email-manage__deny-toolbar">
              <p className="admin-board-manage__desc" style={{ marginBottom: 0 }}>
                saveDenyList 전체 {denyListItems.length}건
                {denyDuplicateInfo.duplicateCount > 0
                  ? ` · 중복 이메일 ${denyDuplicateInfo.duplicateCount}건`
                  : ''}
              </p>
              <div className="recruit-email-manage__deny-toolbar-actions">
                <button
                  type="button"
                  className="admin-btn delete-btn"
                  disabled={denyListLoading || denyDedupeLoading || denyDuplicateInfo.duplicateCount === 0}
                  onClick={() => void handleDeduplicateDenyEmails()}
                >
                  {denyDedupeLoading ? '처리 중...' : `중복 이메일 삭제 (${denyDuplicateInfo.duplicateCount})`}
                </button>
              </div>
            </div>

            <div className="recruit-email-manage__deny-add-row">
              <label className="recruit-email-manage__test-email-field">
                <span>교회명</span>
                <input
                  type="text"
                  value={denyAddChurch}
                  onChange={(e) => setDenyAddChurch(e.target.value)}
                  disabled={denyListLoading || denyAddLoading || denyDedupeLoading}
                  placeholder="선택 입력"
                />
              </label>
              <label className="recruit-email-manage__test-email-field">
                <span>이메일</span>
                <input
                  type="email"
                  value={denyAddEmail}
                  onChange={(e) => setDenyAddEmail(e.target.value)}
                  disabled={denyListLoading || denyAddLoading || denyDedupeLoading}
                  placeholder="email@example.com"
                />
              </label>
              <button
                type="button"
                className="admin-btn send-btn"
                disabled={denyListLoading || denyAddLoading || denyDedupeLoading || !denyAddEmail.trim()}
                onClick={() => void handleAddDenyEmail()}
              >
                {denyAddLoading ? '추가 중...' : '메일추가'}
              </button>
            </div>

            {denyListLoading ? (
              <Loading />
            ) : (
              <div className="admin-board-manage__table-wrap">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>번호</th>
                      <th>교회명</th>
                      <th>이메일</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {denyListItems.length > 0 ? (
                      denyListItems.map((item, index) => {
                        const isDuplicate = denyDuplicateInfo.duplicateIds.has(item.id);
                        const isKeptDuplicate = denyDuplicateInfo.keptIds.has(item.id);

                        return (
                          <tr
                            key={item.id}
                            className={isDuplicate ? 'recruit-email-manage__deny-row--duplicate' : undefined}
                          >
                            <td>{index + 1}</td>
                            <td style={{ textAlign: 'left' }} title={item.church}>
                              {item.church || '-'}
                            </td>
                            <td
                              className="admin-board-manage__account"
                              style={{ textAlign: 'left' }}
                              title={item.email}
                            >
                              {item.email || '-'}
                            </td>
                            <td>
                              {isDuplicate ? (
                                <span className="recruit-email-manage__deny-badge recruit-email-manage__deny-badge--duplicate">
                                  중복(삭제대상)
                                </span>
                              ) : isKeptDuplicate ? (
                                <span className="recruit-email-manage__deny-badge recruit-email-manage__deny-badge--kept">
                                  유지
                                </span>
                              ) : (
                                '-'
                              )}
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '24px', color: '#666' }}>
                          등록된 전송금지 항목이 없습니다.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
